import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/Table'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogClose 
} from '../ui/Dialog'
import { Input } from '../ui/Input'
import { SearchInput } from '../ui/SearchInput'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/Select'
import {
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  Building2,
  Truck,
  MapPin,
  Users,
  Calendar,
  Phone,
  Mail,
  FileText,
  DollarSign,
  Search,
  X
} from 'lucide-react'
import authService from '../../services/authService'
import PricingConfigModal from '../pricing/PricingConfigModal'
import DeleteHospitalModal from './DeleteHospitalModal'
import Pagination from '../ui/Pagination'
import TotalPageNumber from '../ui/TotalPageNumber'
import { usePagination } from '../../hooks/usePagination'

// View Company Modal Component
const ViewCompanyModal = ({ company, open, onOpenChange }) => {
  if (!company) return null

  const getTypeIcon = (type) => {
    return type === "hospital" ? <Building2 className="h-5 w-5" /> : <Truck className="h-5 w-5" />
  }

  const getTypeColor = (type) => {
    return type === "hospital" ? "text-blue-600" : "text-green-600"
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl mx-4 sm:mx-0 max-h-[90vh] overflow-y-auto p-0 rounded-2xl shadow-2xl border-0 bg-white">
        <DialogClose className="absolute right-4 top-4 z-10 rounded-full p-1.5 hover:bg-gray-100 transition-colors duration-200" />
        
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-gray-900">
            {getTypeIcon(company.type)}
            <span className={`${getTypeColor(company.type)} break-words`}>{company.name}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            View detailed information about {company.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 px-6 py-6">
          {/* Company Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Company ID</p>
                  <p className="text-sm text-gray-900 font-mono break-all">{company.id}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Type</p>
                  <Badge variant="outline" className="text-xs w-fit">
                    {company.type === "hospital" ? "🏥 Hospital" : "🚛 Fleet Operator"}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Location</p>
                  <p className="text-sm text-gray-900 break-words">{company.location}</p>
                </div>
              </div>

              {(company.license || company.business_license || company.license_number) && (
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium mb-1">
                      {company.type === "hospital" ? "Hospital License" : "Business / Fleet License"}
                    </p>
                    <p className="text-sm text-gray-900 font-mono break-all">
                      {company.license || company.business_license || company.license_number}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Total Users</p>
                  <p className="text-sm text-gray-900 font-mono">{company.users}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Total Requests</p>
                  <p className="text-sm text-gray-900 font-mono">{company.requests}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Joined</p>
                  <p className="text-sm text-gray-900">
                    {company.joinedDate ? new Date(company.joinedDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <Badge variant={company.status === 'active' ? 'default' : company.status === 'pending' ? 'secondary' : 'destructive'} className="text-xs">
                {company.status}
              </Badge>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Additional Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Company Type</p>
                <p className="text-sm text-gray-900 break-words">
                  {company.type === "hospital" ? "Medical Facility" : "Transportation Service"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Registration Date</p>
                <p className="text-sm text-gray-900">
                  {company.joinedDate ? new Date(company.joinedDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Edit Company Modal Component
const EditCompanyModal = ({ company, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    status: 'active'
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        location: company.location || '',
        status: company.status || 'active'
      })
    }
  }, [company])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      await onSave(company.id, formData)
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating company:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!company) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md mx-4 sm:mx-0 max-h-[90vh] overflow-y-auto p-0 rounded-2xl shadow-2xl border-0 bg-white">
        <DialogClose className="absolute right-4 top-4 z-10 rounded-full p-1.5 hover:bg-gray-100 transition-colors duration-200" />
        
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-gray-900">
            <Edit className="h-5 w-5 text-gray-600" />
            <span className="break-words">Edit {company.name}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Update company information
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Company Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter company name"
              required
              className="text-sm rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Location</label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Enter location"
              required
              className="text-sm rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="text-sm w-full sm:w-auto rounded-lg border-gray-200 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="text-sm w-full sm:w-auto rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const CompaniesTable = ({ type = 'all' }) => {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { page, setPage, limit, setLimit, handleLimitChange } = usePagination();
  const [serverPagination, setServerPagination] = useState(null) // { page, limit, total, totalPages }
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce search input by 300ms so we don't bombard the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch companies data function (extracted for reuse)
  const fetchCompanies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      let response
      if (type === 'hospitals') {
        // Fetch hospitals with pagination and server-side search
        const hospitalParams = { page, limit }
        if (debouncedSearchQuery.trim()) {
          hospitalParams.search = debouncedSearchQuery.trim()
        }
        const hospitalsResponse = await authService.getAllHospitals(hospitalParams)
        const pag = hospitalsResponse?.meta?.pagination || hospitalsResponse?.data?.pagination || {}
        const limitFromApi = Number(pag.limit || limit || 10)
        const pageFromApi = Number(pag.page || page || 1)
        const totalFromApi = pag.total != null ? Number(pag.total) : null
        const pagesFromApi = pag.pages != null ? Number(pag.pages) : null
        // Transform hospitals data to match CompaniesTable format
        const hospitals = hospitalsResponse.data.map(hospital => ({
          ...hospital,
          id: hospital.hospital_id,
          name: hospital.name,
          type: "hospital",
          license: hospital.license_number || hospital.license || "",
          license_number: hospital.license_number || "",
          location: hospital.address || `${hospital.city || ''}, ${hospital.state || ''}`.trim() || "N/A",
          status: hospital.status || hospital.partnership_status || "active",
          users: hospital.user_count || 0,
          requests: hospital.booking_count || 0,
          joinedDate: hospital.createdAt || hospital.created_at
        }))
        response = { data: hospitals }
        // Some responses don't include total/pages; if pages is null but returned a full page, allow "next" by setting totalPages to page+1.
        const computedTotalPages = (() => {
          if (pagesFromApi && pagesFromApi > 0) return pagesFromApi
          if (typeof totalFromApi === 'number' && totalFromApi >= 0) return Math.max(1, Math.ceil(totalFromApi / (limitFromApi || 10)))
          return hospitals.length >= limitFromApi ? pageFromApi + 1 : pageFromApi
        })()
        setServerPagination({
          page: pageFromApi,
          limit: limitFromApi,
          total: typeof totalFromApi === 'number' ? totalFromApi : null,
          totalPages: computedTotalPages
        })
      } else if (type === 'operators') {
        // Fetch only fleet operators with pagination and server-side search
        const fleetParams = { page, limit }
        if (debouncedSearchQuery.trim()) {
          fleetParams.search = debouncedSearchQuery.trim()
        }
        const fleetResponse = await authService.getAllFleetOperators(fleetParams)
        const pag = fleetResponse?.meta?.pagination || fleetResponse?.data?.pagination || {}
        const limitFromApi = Number(pag.limit || limit || 10)
        const pageFromApi = Number(pag.page || page || 1)
        const totalFromApi = pag.total != null ? Number(pag.total) : null
        const pagesFromApi = pag.pages != null ? Number(pag.pages) : (pag.totalPages != null ? Number(pag.totalPages) : null)
        // Transform fleet operators data to match CompaniesTable format
        const fleetOperators = (fleetResponse.data || []).map(fleet => ({
          ...fleet,
          id: fleet.operator_id,
          name: fleet.name,
          type: "fleet",
          license: fleet.business_license || fleet.license_number || fleet.fleet_license || fleet.license || "",
          business_license: fleet.business_license || "",
          location: fleet.address || `${fleet.city || ''}, ${fleet.state || ''}`.trim() || "N/A",
          status: fleet.status || fleet.partnership_status || "active",
          users: fleet.user_count ?? (fleet.users ? fleet.users.length : 0),
          requests: fleet.booking_count || 0,
          joinedDate: fleet.createdAt || fleet.created_at
        }))
        response = { data: fleetOperators }
        const computedTotalPages = (() => {
          if (pagesFromApi && pagesFromApi > 0) return pagesFromApi
          if (typeof totalFromApi === 'number' && totalFromApi >= 0) return Math.max(1, Math.ceil(totalFromApi / (limitFromApi || 10)))
          return fleetOperators.length >= limitFromApi ? pageFromApi + 1 : pageFromApi
        })()
        setServerPagination({
          page: pageFromApi,
          limit: limitFromApi,
          total: typeof totalFromApi === 'number' ? totalFromApi : null,
          totalPages: computedTotalPages
        })
      } else {
        // Default: fetch all companies (backward compatibility)
        response = await authService.getAllCompanies()
      }
      
      setCompanies(response.data || [])
    } catch (err) {
      console.error('Error fetching companies:', err)
      // Extract more detailed error message
      const errorMessage = err?.response?.data?.message || 
                          (err?.message ? `Failed to load companies: ${err.message}` : 'Failed to load companies data. Please try again.')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Fetch companies data on component mount and whenever type, page, limit, or debounced search query changes
  useEffect(() => {
    fetchCompanies()
  }, [type, page, limit, debouncedSearchQuery])

  // Reset pagination when switching type (Hospitals / Operators / All)
  useEffect(() => {
    setPage(1)
    setLimit(10)
    setServerPagination(null)
    setSearchQuery('')
    setDebouncedSearchQuery('')
  }, [type])

  // Reset page to 1 when search query changes
  useEffect(() => {
    setPage(1)
  }, [searchQuery])

  // Filter companies client-side based on search query
  const filteredCompanies = useMemo(() => {
    if (!Array.isArray(companies)) return []
    if (!searchQuery.trim()) return companies
    const q = searchQuery.trim().toLowerCase()
    return companies.filter((company) => {
      const name = (company.name || '').toLowerCase()
      const location = (company.location || '').toLowerCase()
      const status = (company.status || '').toLowerCase()
      const typeStr = (company.type || '').toLowerCase()
      const license = (company.license || company.business_license || company.license_number || company.fleet_license || '').toLowerCase()
      const contact = (company.contact_info || company.contact_person || company.emergency_contact || company.phone || '').toLowerCase()
      const city = (company.city || '').toLowerCase()
      const state = (company.state || '').toLowerCase()
      const id = String(company.id || '').toLowerCase()
      return (
        name.includes(q) ||
        location.includes(q) ||
        status.includes(q) ||
        typeStr.includes(q) ||
        license.includes(q) ||
        contact.includes(q) ||
        city.includes(q) ||
        state.includes(q) ||
        id.includes(q)
      )
    })
  }, [companies, searchQuery])

  const isServerPaginated = type === 'operators' || type === 'hospitals'

  const totalPages = useMemo(() => {
    if (isServerPaginated && serverPagination?.totalPages) {
      return serverPagination.totalPages
    }
    const safeLimit = Number(limit || 10)
    const total = Array.isArray(filteredCompanies) ? filteredCompanies.length : 0
    return Math.max(1, Math.ceil(total / safeLimit))
  }, [isServerPaginated, serverPagination?.totalPages, filteredCompanies.length, limit])

  // Keep page in range if total changes (e.g. after deletion or search filter reduction)
  useEffect(() => {
    if (!loading && totalPages > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [totalPages, page, loading, setPage])

  const pagedCompanies = useMemo(() => {
    if (!Array.isArray(filteredCompanies) || filteredCompanies.length === 0) return []
    // For server-side paginated tables ('operators' and 'hospitals'), the backend already handles pagination
    if (isServerPaginated) {
      return filteredCompanies
    }
    // Client-side pagination for unpaginated endpoints (type === 'all')
    const safeLimit = Number(limit || 10)
    const safePage = Math.min(Math.max(1, Number(page || 1)), totalPages)
    const start = (safePage - 1) * safeLimit
    return filteredCompanies.slice(start, start + safeLimit)
  }, [isServerPaginated, filteredCompanies, limit, page, totalPages])

  const getStatusBadge = (status) => {
    const variants = {
      active: "default",
      pending: "secondary", 
      suspended: "destructive"
    }
    return <Badge variant={variants[status]} className="text-xs sm:text-sm">{status}</Badge>
  }

  const getTypeBadge = (type) => {
    return (
      <Badge variant="outline" className="text-xs sm:text-sm">
        {type === "hospital" ? "🏥 Hospital" : "🚛 Fleet"}
      </Badge>
    )
  }

  // Action button handlers
  const handleViewCompany = (company) => {
    setSelectedCompany(company)
    setViewModalOpen(true)
  }

  const handleEditCompany = (company) => {
    setSelectedCompany(company)
    setEditModalOpen(true)
  }

  const handleSaveCompany = async (companyId, formData) => {
    try {
      // Determine if it's a hospital or fleet operator
      const company = companies.find(c => c.id === companyId)
      if (!company) throw new Error('Company not found')

      // Detect specifically which fields were modified by the user
      const updatedFields = []
      const isNameChanged = (formData.name || '').trim() !== (company.name || '').trim()
      const isLocationChanged = (formData.location || '').trim() !== (company.location || '').trim()
      const isStatusChanged = (formData.status || '').toLowerCase() !== (company.status || '').toLowerCase()

      if (isNameChanged) updatedFields.push('Company name')
      if (isLocationChanged) updatedFields.push('Location')
      if (isStatusChanged) updatedFields.push('Status')

      // Map form data to backend expected fields
      const statusValue = formData.status ? formData.status.toUpperCase() : undefined
      let updateData = {}
      
      if (company.type === 'hospital') {
        updateData = {
          name: formData.name,
          address: formData.location,
          ...(statusValue && { partnership_status: statusValue })
        }
      } else {
        updateData = {
          name: formData.name,
          address: formData.location,
          ...(statusValue && { partnership_status: statusValue })
        }
      }

      let response
      if (company.type === 'hospital') {
        response = await authService.updateHospital(companyId, updateData)
      } else {
        response = await authService.updateFleetOperator(companyId, updateData)
      }

      if (response.success) {
        // Update local state with the original form data for display
        setCompanies(companies.map(c => 
          c.id === companyId 
            ? { ...c, ...formData }
            : c
        ))

        // Determine specific user-friendly success message based on what was updated
        let successMessage = 'Company updated successfully!'
        if (updatedFields.length === 1) {
          successMessage = `${updatedFields[0]} updated successfully!`
        } else if (updatedFields.length === 2) {
          successMessage = `${updatedFields[0]} and ${updatedFields[1].toLowerCase()} updated successfully!`
        } else if (updatedFields.length >= 3) {
          const lastField = updatedFields.slice(-1)[0]
          const otherFields = updatedFields.slice(0, -1)
          successMessage = `${otherFields.join(', ')} and ${lastField.toLowerCase()} updated successfully!`
        }

        alert(successMessage)
      } else {
        throw new Error(response.message || 'Failed to update company')
      }
    } catch (error) {
      console.error('Error updating company:', error)
      const detail = error.errors?.map(e => e.message).join('\n') || error.message || 'Unknown error'
      alert(`Error updating company: ${detail}`)
      throw error
    }
  }

  const handleDeleteCompany = (company) => {
    // Only show modal for hospitals, keep existing behavior for fleet operators
    if (company.type === 'hospital') {
      setSelectedCompany(company)
      setDeleteModalOpen(true)
    } else {
      // For fleet operators, use existing window.confirm (can be updated later)
      if (!window.confirm(`Are you sure you want to delete ${company.name}? This action cannot be undone.`)) {
        return
      }
      performDeleteCompany(company)
    }
  }

  const performDeleteCompany = async (company) => {
    setDeleteLoading(company.id)

    try {
      let response
      if (company.type === 'hospital') {
        response = await authService.deleteHospital(company.id)
      } else {
        response = await authService.deleteFleetOperator(company.id)
      }

      if (response.success) {
        // Remove from local state
        setCompanies(companies.filter(c => c.id !== company.id))
        // Refresh the list
        fetchCompanies()
      } else {
        throw new Error(response.message || 'Failed to delete company')
      }
    } catch (error) {
      console.error('Error deleting company:', error)
      alert(`Error deleting company: ${error.message}`)
    } finally {
      setDeleteLoading(null)
    }
  }

  const handleDeleteSuccess = () => {
    // Refresh the companies list
    fetchCompanies()
  }

  const handleConfigurePricing = (company) => {
    setSelectedCompany(company)
    setPricingModalOpen(true)
  }

  const handleSavePricing = async (operatorId, pricingData) => {
    // Pricing is saved in the modal, this callback is just for notifications
    console.log('Pricing saved for operator:', operatorId)
  }

  return (
    <div className="space-y-4 w-full">
      {/* View Company Modal */}
      <ViewCompanyModal
        company={selectedCompany}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
      />

      {/* Edit Company Modal */}
      <EditCompanyModal
        company={selectedCompany}
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSave={handleSaveCompany}
      />

      {/* Pricing Configuration Modal */}
      <PricingConfigModal
        operator={selectedCompany}
        open={pricingModalOpen}
        onOpenChange={setPricingModalOpen}
        onSave={handleSavePricing}
      />

      {/* Delete Hospital Modal */}
      {selectedCompany && selectedCompany.type === 'hospital' && (
        <DeleteHospitalModal
          hospital={selectedCompany}
          open={deleteModalOpen}
          onOpenChange={(open) => {
            setDeleteModalOpen(open)
            if (!open) {
              setSelectedCompany(null)
            }
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {/* Search Bar */}
      <div className="w-full sm:max-w-md">
        <SearchInput
          placeholder={
            type === 'hospitals'
              ? 'Search hospitals by name, license, city, or status...'
              : type === 'operators'
              ? 'Search fleet operators by name, license, city, or status...'
              : 'Search companies by name, license, location, or status...'
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-muted-foreground">Loading companies...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-destructive mr-2" />
            <span className="text-destructive">{error}</span>
          </div>
          <Button 
            onClick={fetchCompanies} 
            variant="outline" 
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Retrying...
              </>
            ) : (
              'Retry'
            )}
          </Button>
        </div>
      ) : companies.length === 0 && !searchQuery.trim() ? (
        <div className="flex items-center justify-center py-12">
          <span className="text-muted-foreground">No companies found.</span>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
          <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900">
            No {type === 'hospitals' ? 'hospitals' : type === 'operators' ? 'fleet operators' : 'companies'} found
          </p>
          <p className="text-xs text-gray-500 mt-1">No results match &ldquo;{searchQuery}&rdquo;.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearchQuery('')}
            className="mt-3 text-xs"
          >
            Clear search
          </Button>
        </div>
      ) : (
        <>
          {/* Pagination controls (always visible) */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-xs text-gray-500">
          {(() => {
            const totalRaw = serverPagination?.total
            const total = typeof totalRaw === 'number' ? totalRaw : Number(companies.length ?? 0)
            const currentPage = Number(page || serverPagination?.page || 1)
            const currentLimit = Number(limit || serverPagination?.limit || 10)
            const start = total === 0 ? 0 : (currentPage - 1) * currentLimit + 1
            const end = typeof totalRaw === 'number'
              ? Math.min(currentPage * currentLimit, total)
              : Math.min(start + (companies.length || 0) - 1, currentPage * currentLimit)
            return typeof totalRaw === 'number'
              ? `Showing ${start}-${end} of ${total}`
              : `Showing ${start}-${end}`
          })()}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <TotalPageNumber
            totalPages={totalPages}
            itemsPerPage={limit}
            onItemsPerPageChange={(n) => {
              handleLimitChange(n)
            }}
          />
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs sm:text-sm">Company</TableHead>
              <TableHead className="text-xs sm:text-sm">Type</TableHead>
              <TableHead className="text-xs sm:text-sm">Location</TableHead>
              <TableHead className="text-xs sm:text-sm">Status</TableHead>
              <TableHead className="text-xs sm:text-sm">Users</TableHead>
              <TableHead className="text-xs sm:text-sm">Requests</TableHead>
              <TableHead className="text-xs sm:text-sm">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedCompanies.map((company) => (
              <TableRow key={company.id}>
                <TableCell className="text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    {company.type === "hospital" ? (
                      <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    ) : (
                      <Truck className="h-4 w-4 text-green-600 flex-shrink-0" />
                    )}
                    <div>
                      <span className="font-medium">{company.name}</span>
                      {(company.license || company.business_license || company.license_number) && (
                        <div className="text-[11px] text-gray-500 font-mono">
                          Lic: {company.license || company.business_license || company.license_number}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  {getTypeBadge(company.type)}
                </TableCell>
                <TableCell className="text-xs sm:text-sm">{company.location}</TableCell>
                <TableCell className="text-xs sm:text-sm">
                  {getStatusBadge(company.status)}
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  <span className="font-mono">{company.users}</span>
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  <span className="font-mono">{company.requests}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-8 sm:w-8"
                      onClick={() => handleViewCompany(company)}
                      title="View company details"
                    >
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-8 sm:w-8"
                      onClick={() => handleEditCompany(company)}
                      title="Edit company"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                    {company.type !== 'hospital' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 sm:h-8 sm:w-8 text-green-600"
                        onClick={() => handleConfigurePricing(company)}
                        title="Configure pricing"
                      >
                        <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-8 sm:w-8 text-destructive"
                      onClick={() => handleDeleteCompany(company)}
                      title="Delete company"
                      disabled={deleteLoading === company.id}
                    >
                      {deleteLoading === company.id ? (
                        <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Tablet Table View */}
      <div className="hidden md:block lg:hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs sm:text-sm">Company</TableHead>
              <TableHead className="text-xs sm:text-sm">Type</TableHead>
              <TableHead className="text-xs sm:text-sm">Status</TableHead>
              <TableHead className="text-xs sm:text-sm">Users</TableHead>
              <TableHead className="text-xs sm:text-sm">Requests</TableHead>
              <TableHead className="text-xs sm:text-sm">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedCompanies.map((company) => (
              <TableRow key={company.id}>
                <TableCell className="text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    {company.type === "hospital" ? (
                      <Building2 className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Truck className="h-4 w-4 text-green-600" />
                    )}
                    <div>
                      <div className="font-medium">{company.name}</div>
                      <div className="text-xs text-muted-foreground">{company.location}</div>
                      {(company.license || company.business_license || company.license_number) && (
                        <div className="text-[11px] text-gray-500 font-mono">
                          Lic: {company.license || company.business_license || company.license_number}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  {getTypeBadge(company.type)}
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  {getStatusBadge(company.status)}
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  <span className="font-mono">{company.users}</span>
                </TableCell>
                <TableCell className="text-xs sm:text-sm">
                  <span className="font-mono">{company.requests}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleViewCompany(company)}
                      title="View company details"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleEditCompany(company)}
                      title="Edit company"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    {company.type !== 'hospital' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-green-600"
                        onClick={() => handleConfigurePricing(company)}
                        title="Configure pricing"
                      >
                        <DollarSign className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => handleDeleteCompany(company)}
                      title="Delete company"
                      disabled={deleteLoading === company.id}
                    >
                      {deleteLoading === company.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {pagedCompanies.map((company) => (
          <div key={company.id} className="border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {company.type === "hospital" ? (
                  <Building2 className="h-5 w-5 text-blue-600" />
                ) : (
                  <Truck className="h-5 w-5 text-green-600" />
                )}
                <div>
                  <div className="font-medium text-sm">{company.name}</div>
                  <div className="text-xs text-muted-foreground">{company.location}</div>
                  {(company.license || company.business_license || company.license_number) && (
                    <div className="text-[11px] text-gray-500 font-mono mt-0.5">
                      Lic: {company.license || company.business_license || company.license_number}
                    </div>
                  )}
                </div>
              </div>
              {getStatusBadge(company.status)}
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <div className="mt-1">{getTypeBadge(company.type)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Users:</span>
                <span className="ml-1 font-mono">{company.users}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Requests:</span>
                <span className="ml-1 font-mono">{company.requests}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Joined:</span>
                <div className="text-xs">
                  {company.joinedDate ? new Date(company.joinedDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-1 mt-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => handleViewCompany(company)}
                title="View company details"
              >
                <Eye className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => handleEditCompany(company)}
                title="Edit company"
              >
                <Edit className="h-3 w-3" />
              </Button>
              {company.type !== 'hospital' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-green-600"
                  onClick={() => handleConfigurePricing(company)}
                  title="Configure pricing"
                >
                  <DollarSign className="h-3 w-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={() => handleDeleteCompany(company)}
                title="Delete company"
                disabled={deleteLoading === company.id}
              >
                {deleteLoading === company.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  )}
</div>
  )
}

CompaniesTable.propTypes = {
  type: PropTypes.oneOf(['all', 'hospitals', 'operators'])
}

export default CompaniesTable
